import { useEffect } from 'react'

import { getAddressCaught } from '@ambire-common/utils/getAddressCaught'
import useDomainsControllerState from '@web/hooks/useDomainsController/useDomainsController'

interface Props {
  address: string
}

const isRailgunAddress = (address: string) => address.trim().toLowerCase().startsWith('0zk1')

const useReverseLookup = ({ address }: Props) => {
  // Don't run getAddress (ethers) on Railgun 0zk addresses; ENS doesn't apply
  const isRailgun = isRailgunAddress(address)
  const checksummedAddress = isRailgun ? '' : getAddressCaught(address)

  const {
    state: { domains, loadingAddresses },
    reverseLookup
  } = useDomainsControllerState()
  const isLoading = loadingAddresses.includes(checksummedAddress)
  const addressInDomains = domains[checksummedAddress]

  useEffect(() => {
    if (!checksummedAddress || addressInDomains || isLoading) return

    reverseLookup(checksummedAddress)
  }, [checksummedAddress, addressInDomains, isLoading, reverseLookup])

  if (isRailgun) {
    return { isLoading: false, ens: undefined }
  }

  // Only report "loading" while a reverse lookup is actually in flight. Do NOT
  // gate on `!addressInDomains` — when the lookup can never populate a domains
  // entry (e.g. the worker has no Ethereum provider, so DomainsController's
  // reverseLookup early-returns without writing one), gating on that flag left
  // `isLoading` stuck `true` forever, so consumers like the account-picker row
  // (`Account.tsx` renders a SkeletonLoader while resolving) showed a permanent
  // skeleton and never revealed the address. Render the address immediately; the
  // ENS name fills in if/when it resolves.
  return {
    isLoading,
    ens: addressInDomains?.ens
  }
}

export default useReverseLookup
